import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import { apiGet, apiPost, operationsClient } from '@/lib/api';
import { reviewEndpoints } from '@/lib/api/endpoints';
import { jobKeys } from '@/lib/queryKeys';

/** NOT-339 §9 — per-visit review status row (keyed by appointment). */
export interface JobReviewSummary {
  id: string;
  appointmentId: string;
  channel: string;
  createdAt: string;
  sentAt: string | null;
  emailSentAt: string | null;
  smsSentAt: string | null;
  deliveryError: string | null;
  respondedAt: string | null;
  internalRating: number | null;
  internalFeedback: string | null;
  publicReviewNudged: boolean;
  /**
   * Resolved server-side against the tenant's recovery band. It is a separate
   * field rather than `!publicReviewNudged` because a recovery-band customer
   * who taps a public platform is now both nudged and in need of recovery.
   */
  needsRecovery: boolean;
  publicReviewRequestPlatform: string | null;
  manualOverride: boolean;
  techId: string | null;
}

/** All review requests for a recurring Job, one per visit that's been asked. */
export function useJobReviews(jobId: string, enabled = true) {
  return useApiQuery<JobReviewSummary[]>({
    queryKey: jobKeys.reviews(jobId),
    queryFn: () => apiGet<JobReviewSummary[]>(operationsClient, reviewEndpoints.jobReviews(jobId)),
    enabled: Boolean(jobId) && enabled,
  });
}

/**
 * Manual "Request review" for a completed visit. `overrideOk` re-sends past
 * failing gates (the backend returns the blocking reasons in its 400 when
 * overrideOk is not set — the caller surfaces them in a confirm). A consent
 * gate (customer opted out / do-not-contact) cannot be overridden at all:
 * the backend returns a 403 instead, which the caller must render as a
 * refusal rather than another confirm-to-retry prompt.
 */
export function useRequestVisitReview(jobId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<
    { reviewRequestId: string; overriddenGates: string[] },
    { appointmentId: string; overrideOk?: boolean }
  >({
    mutationFn: ({ appointmentId, overrideOk }) =>
      apiPost(operationsClient, reviewEndpoints.manualTrigger(appointmentId), {
        overrideOk: overrideOk ?? false,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobKeys.reviews(jobId) });
      toast.success('Review request queued');
    },
    // Gate-blocked sends return a 400 (overridable) or a 403 (consent block,
    // never overridable) with the reasons; let the caller decide how to
    // render that — a confirm-to-retry for the 400, a refusal for the 403.
    // Suppress the default error toast so the dialog owns the messaging.
    showErrorToast: false,
  });
}
